// BookTickets/SummaryBox/index.tsx
'use client'

import React, { useState, useCallback, useMemo, ReactElement } from 'react'
import cn from 'classnames'
import styles from './index.module.scss'

import ContinueButton from '@booking/components/ContinueButton'
import { useBookingState } from '@booking/state/booking.context'

import { SummaryHeader } from './components/SummaryHeader'
import { SummaryFields } from './components/SummaryFields'
import { SummaryTotals } from './components/SummaryTotals'
import { useSummaryData } from './hooks/useSummaryData'
import { useTranslations } from 'next-intl'
import TicketsList from './components/TicketList'
import { stageNumberOf } from '../../lib/bookingConfig'
import ExtraLine from './components/ExtraLine'
import AddonsList from './components/AddonsList'

type SummaryBoxProps = {
  onContinue?: () => void
  className?: string
  simple?: boolean
}

export type SummaryField = {
  name: string
  value: string | ReactElement
  alignable: boolean
  show: boolean
}

export default function SummaryBox({ onContinue, className, simple }: SummaryBoxProps) {
  const [open, setOpen] = useState(true)
  const t = useTranslations('booking')
  const { stage, config } = useBookingState()
  const hasExtras = Boolean(config?.extras)

  const summaryData = useSummaryData() // custom hook, returns all processed data

  const onToggleOpen = useCallback(() => setOpen((v) => !v), [])
  const onContinueWrapped = useCallback(() => {
    return onContinue?.()
  }, [onContinue])

  const availableFields: SummaryField[] = useMemo(
    () => [
      {
        name: hasExtras ? 'Pack' : t('ticket.summaryBox.ticketType'),
        value: t(config.text.ticketTypeLabelKey),
        alignable: true,
        show: true,
      },
      {
        name: config.extras
          ? t(
              config.extras.summaryLabelKey ??
                config.extras.titleKey ??
                'ticket.summaryBox.extrasBreakdown',
            )
          : t('ticket.summaryBox.extrasBreakdown'),
        value: (
          <>
            {summaryData.extrasLines.map((line, i) => (
              <ExtraLine key={i} {...line} />
            ))}
          </>
        ),
        alignable: true,
        show: summaryData.hasExtras,
      },
      {
        name: hasExtras ? t('ticket.summaryBox.breakdownAlt') : t('ticket.summaryBox.breakdown'),
        value: <TicketsList lines={summaryData.ticketLines} />,
        alignable: true,
        show: summaryData.hasTickets,
      },
      {
        name: t('ticket.summaryBox.date'),
        value: summaryData.ticketDate,
        alignable: true,
        show: summaryData.hasTicketDate,
      },
      {
        name: t('ticket.summaryBox.time'),
        value: summaryData.ticketTime,
        alignable: true,
        show: summaryData.hasTicketTime,
      },
      {
        name: t('ticket.summaryBox.addons'),
        value: <AddonsList />,
        alignable: true,
        show: summaryData.hasAddons,
      },
    ],
    [
      t,
      config,
      hasExtras,
      summaryData.extrasLines,
      summaryData.hasExtras,
      summaryData.ticketLines,
      summaryData.ticketDate,
      summaryData.ticketTime,
      summaryData.hasTickets,
      summaryData.hasTicketDate,
      summaryData.hasTicketTime,
      summaryData.hasAddons,
    ],
  )
  return (
    <div className={cn(styles.summaryBox, className, simple && styles.simple)} id="summary-box">
      <SummaryHeader open={open} onToggleOpen={onToggleOpen} />
      {open && (
        <div className={styles.body}>
          <SummaryFields availableFields={availableFields} />
          {stage && stage > stageNumberOf(config, 'datetime') && (
            <SummaryTotals summaryData={summaryData} />
          )}
          {onContinue && (
            <ContinueButton
              onContinue={onContinueWrapped}
              className={styles.continueButton}
              shake={summaryData.shake}
            />
          )}
        </div>
      )}
    </div>
  )
}
